import React, { useEffect, useRef, useState } from "react";

const MIN_SCALE = 0.5;
const MAX_SCALE = 6;

/**
 * 将图片缩放比例限制在允许范围内。
 * @param {number} scale 原始缩放比例
 * @returns {number} 限制后的缩放比例
 */
const clampScale = (scale) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));

/**
 * 渲染支持滚轮缩放、拖动平移和复位的工程图片。
 * @param {{ src?: string, alt: string, dark?: boolean }} props 组件属性
 * @returns {React.JSX.Element} 工程图片查看器
 */
const PanZoomAsset = ({ src, alt, dark = false }) => {
  const viewportRef = useRef(null);
  const dragRef = useRef({ active: false, x: 0, y: 0 });
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  const [loadError, setLoadError] = useState(false);

  /**
   * 按指定倍数调整当前图片缩放比例。
   * @param {number} factor 缩放倍数
   */
  const zoom = (factor) => {
    setTransform((current) => ({
      ...current,
      scale: clampScale(current.scale * factor),
    }));
  };

  /**
   * 恢复图片的初始缩放和位移。
   */
  const reset = () => {
    setTransform({ scale: 1, x: 0, y: 0 });
  };

  /**
   * 开始记录鼠标或触控拖动位置。
   * @param {React.PointerEvent<HTMLDivElement>} event 指针事件
   */
  const handlePointerDown = (event) => {
    if (event.button !== 0 || event.target.closest("button")) return;
    dragRef.current = { active: true, x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  /**
   * 根据指针移动距离更新图片平移位置。
   * @param {React.PointerEvent<HTMLDivElement>} event 指针事件
   */
  const handlePointerMove = (event) => {
    if (!dragRef.current.active) return;
    const deltaX = event.clientX - dragRef.current.x;
    const deltaY = event.clientY - dragRef.current.y;
    dragRef.current = { active: true, x: event.clientX, y: event.clientY };
    setTransform((current) => ({
      ...current,
      x: current.x + deltaX,
      y: current.y + deltaY,
    }));
  };

  /**
   * 结束当前图片拖动。
   */
  const stopDragging = () => {
    dragRef.current.active = false;
  };

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    /**
     * 使用非被动滚轮监听器实现图片缩放，避免页面同时滚动。
     * @param {WheelEvent} event 滚轮事件
     */
    const handleWheel = (event) => {
      event.preventDefault();
      zoom(event.deltaY < 0 ? 1.1 : 1 / 1.1);
    };

    viewport.addEventListener("wheel", handleWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", handleWheel);
  }, []);

  useEffect(() => {
    setLoadError(false);
    setTransform({ scale: 1, x: 0, y: 0 });
  }, [src]);

  return (
    <div
      ref={viewportRef}
      className={`relative h-[320px] touch-none overflow-hidden border border-gray-200 ${
        dark ? "bg-[#0d1422]" : "bg-[#f8fbff]"
      }`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
      onLostPointerCapture={stopDragging}
    >
      {src && !loadError ? (
        <div
          className="flex h-full w-full items-center justify-center will-change-transform"
          style={{
            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
          }}
        >
          <img
            src={src}
            alt={alt}
            draggable="false"
            className="max-h-[86%] max-w-[86%] select-none object-contain"
            onError={() => setLoadError(true)}
          />
        </div>
      ) : (
        <div className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-500">
          {loadError ? "Failed to load image" : "No resource available"}
        </div>
      )}

      <div className="absolute bottom-3 right-3 flex items-center gap-1 rounded-lg bg-white/95 p-1 shadow-md">
        <button type="button" className="h-8 w-8 rounded hover:bg-gray-100" onClick={() => zoom(1 / 1.25)} aria-label="Zoom out">
          −
        </button>
        <span className="min-w-[50px] text-center text-xs text-gray-600">
          {Math.round(transform.scale * 100)}%
        </span>
        <button type="button" className="h-8 w-8 rounded hover:bg-gray-100" onClick={() => zoom(1.25)} aria-label="Zoom in">
          +
        </button>
        <button type="button" className="h-8 rounded px-2 text-xs hover:bg-gray-100" onClick={reset} aria-label="Reset view">
          Reset
        </button>
      </div>
    </div>
  );
};

export default PanZoomAsset;
